import React, { useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Stack, Alert, Table, TableHead, TableRow, TableCell, TableBody, List, ListItem, ListItemText, FormControlLabel, Switch, FormControl, InputLabel, Select, MenuItem, TextField } from '@mui/material';
import { contestAPI } from '../services/api';

const truncate = (text, len = 180) => {
  if (!text) return '';
  if (text.length <= len) return text;
  return text.slice(0, len) + '…';
};

const ContestStats = () => {
  const [stats, setStats] = useState(null);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  // Vote details state
  const [details, setDetails] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [onlyCompleted, setOnlyCompleted] = useState(true);
  const [submissionFilter, setSubmissionFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [avgExpanded, setAvgExpanded] = useState({});

  const toggleRow = (idx) => setExpandedRows((m) => ({ ...m, [idx]: !m[idx] }));

  const handleLoad = async () => {
    try {
      setErr(null); setMsg(null);
      const [statsRes, detailsRes] = await Promise.all([
        contestAPI.getActiveStats(),
        contestAPI.getActiveVoteDetails({ onlyCompletedVoters: true }),
      ]);
      setStats(statsRes.data);
      setDetails(detailsRes.data || []);
      setOnlyCompleted(true);
      setMsg('Статистику та деталі голосів завантажено');
    } catch (e) {
      setErr(e?.response?.data?.message || e.message);
      setStats(null);
      setDetails([]);
    }
  };

  const reloadDetails = async (newOnlyCompleted = onlyCompleted) => {
    try {
      setLoadingDetails(true); setErr(null);
      const res = await contestAPI.getActiveVoteDetails({ onlyCompletedVoters: newOnlyCompleted });
      setDetails(res.data || []);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message);
    } finally {
      setLoadingDetails(false);
    }
  };

  const uniqueSubmissions = useMemo(() => {
    const map = new Map();
    details.forEach(d => {
      if (!map.has(d.submissionId)) map.set(d.submissionId, d.submissionText || '');
    });
    return Array.from(map.entries()).map(([id, text]) => ({ id, text }))
      .sort((a, b) => a.id - b.id);
  }, [details]);

  const filtered = useMemo(() => {
    let arr = details;
    if (submissionFilter !== 'all') {
      arr = arr.filter(d => d.submissionId === Number(submissionFilter));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter(d => (d.voterEmail?.toLowerCase().includes(q) || d.voterFullName?.toLowerCase().includes(q)));
    }
    return arr;
  }, [details, submissionFilter, search]);

  const exportCSV = () => {
    const rows = [['submissionId','submissionText','voterId','voterEmail','voterFullName','score']];
    filtered.forEach(d => {
      const safe = (s) => (s==null?'' : String(s).replaceAll('"','""'));
      rows.push([d.submissionId, d.submissionText, d.voterId, d.voterEmail, d.voterFullName, d.score]);
    });
    const csv = rows.map(r => r.map(c => `"${(c==null?'':String(c)).replaceAll('"','""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student_of_year_vote_details${onlyCompleted?'_counted':''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAvgCSV = () => {
    const rows = [['submissionId','submissionText','averageScore','votesCount']];
    const map = new Map(uniqueSubmissions.map(u => [u.id, u.text || '']));
    (stats?.submissionStats || []).forEach(s => {
      const txt = map.get(s.submissionId) || '';
      rows.push([s.submissionId, txt, s.averageScore == null ? '' : s.averageScore, s.votesCount]);
    });
    const csv = rows.map(r => r.map(c => `"${(c==null?'':String(c)).replaceAll('"','""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student_of_year_avg_scores.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 700 }}>Статистика «Студент року»</Typography>
      {msg && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}
      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button variant="contained" onClick={handleLoad}>Оновити статистику</Button>
          </Stack>
        </CardContent>
      </Card>

      {stats && (
        <Stack spacing={2}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>{stats.contestName || 'Студент року'}</Typography>
              <Typography variant="body2" color="text.secondary">Всього текстів: {stats.totalSubmissions}</Typography>
              <Typography variant="body2" color="text.secondary">Кількість користувачів, які завершили оцінювання: {stats.totalVotersCompleted}</Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>Середні оцінки по текстах</Typography>
                <Button variant="outlined" onClick={exportAvgCSV} disabled={!stats?.submissionStats?.length}>Експорт CSV</Button>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID тексту</TableCell>
                    <TableCell>Текст</TableCell>
                    <TableCell>Середня оцінка</TableCell>
                    <TableCell>Кількість врахованих голосів</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.submissionStats?.map((s) => (
                    <TableRow key={s.submissionId}>
                      <TableCell>#{s.submissionId}</TableCell>
                      <TableCell>
                        {(() => {
                          const fullText = (uniqueSubmissions.find(u => u.id === s.submissionId)?.text) || '';
                          const expanded = !!avgExpanded[s.submissionId];
                          const shownText = expanded ? fullText : truncate(fullText);
                          return (
                            <>
                              <Box sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{shownText}</Box>
                              {(fullText && fullText.length > 180) && (
                                <Button size="small" onClick={() => setAvgExpanded(m => ({ ...m, [s.submissionId]: !m[s.submissionId] }))} sx={{ mt: 0.5 }}>
                                  {expanded ? 'Сховати' : 'Показати повністю'}
                                </Button>
                              )}
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell>{s.averageScore == null ? '-' : s.averageScore}</TableCell>
                      <TableCell>{s.votesCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Vote details for admins */}
          <Card>
            <CardContent>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>Деталі голосів (хто кому яку оцінку поставив)</Typography>
                <FormControlLabel control={<Switch checked={onlyCompleted} onChange={async (e) => { setOnlyCompleted(e.target.checked); await reloadDetails(e.target.checked); }} />} label="Лише зараховані (повні бюлетені)" />
                <FormControl sx={{ minWidth: 220 }} size="small">
                  <InputLabel id="submission-filter-label">Фільтр за текстом</InputLabel>
                  <Select labelId="submission-filter-label" label="Фільтр за текстом" value={submissionFilter} onChange={(e) => setSubmissionFilter(e.target.value)}>
                    <MenuItem value="all">Усі тексти</MenuItem>
                    {uniqueSubmissions.map(s => (
                      <MenuItem key={s.id} value={String(s.id)}>#{s.id} — {truncate(s.text, 80)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField size="small" label="Пошук (ім'я/емейл)" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ width: { xs: '100%', md: 260 } }} />
                <Button variant="outlined" onClick={exportCSV} disabled={(filtered.length===0)}>Експорт CSV</Button>
              </Stack>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={80}>ID тексту</TableCell>
                    <TableCell>Текст</TableCell>
                    <TableCell>Користувач</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell width={80}>Оцінка</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingDetails && (
                    <TableRow><TableCell colSpan={5}>Завантаження…</TableCell></TableRow>
                  )}
                  {!loadingDetails && filtered.map((d, idx) => {
                    const expanded = !!expandedRows[idx];
                    const shownText = expanded ? d.submissionText : truncate(d.submissionText);
                    return (
                      <TableRow key={`${d.submissionId}-${d.voterId}-${idx}`}>
                        <TableCell>#{d.submissionId}</TableCell>
                        <TableCell>
                          <Box sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{shownText}</Box>
                          {(d.submissionText && d.submissionText.length > 180) && (
                            <Button size="small" onClick={() => toggleRow(idx)} sx={{ mt: 0.5 }}>
                              {expanded ? 'Сховати' : 'Показати повністю'}
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>{d.voterFullName || '—'}</TableCell>
                        <TableCell>
                          <span>{d.voterEmail}</span>
                        </TableCell>
                        <TableCell>{d.score}</TableCell>
                      </TableRow>
                    );
                  })}
                  {!loadingDetails && filtered.length === 0 && (
                    <TableRow><TableCell colSpan={5}>Немає даних для відображення</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Користувачі, які завершили оцінювання</Typography>
              <List>
                {stats.voterStatuses?.map((v) => (
                  <ListItem key={v.userId}>
                    <ListItemText primary={v.fullName} secondary={`${v.email} — ${v.votedCount}/${v.totalSubmissions}`} />
                  </ListItem>
                ))}
                {(!stats.voterStatuses || stats.voterStatuses.length === 0) && (
                  <Typography variant="body2" color="text.secondary">Поки немає завершених оцінювань</Typography>
                )}
              </List>
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
};

export default ContestStats;
